import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { Input } from '@core-ui/Components/ui/input';
import { Button } from '@core-ui/Components/ui/button';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@core-ui/Components/ui/input-otp';
import { TendrilColorSloganTyped } from '@assets/index';

export const TOTP_CODE_LENGTH = 6;
const DIGITS_ONLY = '^[0-9]+$';

export interface SecondFactor {
  totpCode?: string;
  backupCode?: string;
}

interface LoginCodeStepProps {
  loading: boolean;
  error: string;
  onSubmit: (secondFactor: SecondFactor) => Promise<void>;
  onBack: () => void;
}

/**
 * Second step of the login for a user with two-factor authentication: the
 * 6-digit code from the authenticator app, or one of the backup codes.
 */
export const LoginCodeStep: React.FC<LoginCodeStepProps> = ({
  loading,
  error,
  onSubmit,
  onBack,
}) => {
  const { t } = useTranslation();
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [backupCode, setBackupCode] = useState('');

  const submit = async (secondFactor: SecondFactor): Promise<void> => {
    await onSubmit(secondFactor);
    // A code is good for one try: start clean after a wrong one.
    setTotpCode('');
    setBackupCode('');
  };

  const canSubmit = useBackupCode
    ? backupCode.trim() !== ''
    : totpCode.length === TOTP_CODE_LENGTH;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (loading || !canSubmit) {
      return;
    }
    void submit(useBackupCode ? { backupCode: backupCode.trim() } : { totpCode });
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="w-full max-w-md space-y-6 px-4 sm:px-0 text-foreground"
      data-qa-id="login-two-factor-form"
    >
      <div className="text-center space-y-1">
        <img
          src={TendrilColorSloganTyped}
          alt="Tendril logo"
          className="mx-auto h-12 w-auto mb-6"
        />
        <h2 className="text-2xl font-semibold">
          {t('loginForm.twoFactor.title')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {useBackupCode
            ? t('loginForm.twoFactor.backupSubtitle')
            : t('loginForm.twoFactor.subtitle')}
        </p>
      </div>

      <div className="space-y-4">
        {useBackupCode ? (
          <Input
            data-qa-id="input-backup-code-login"
            type="text"
            autoFocus
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            placeholder={t('loginForm.twoFactor.backupPlaceholder')}
            value={backupCode}
            disabled={loading}
            onChange={(e) => {
              setBackupCode(e.target.value);
            }}
            className="text-center font-mono tracking-widest"
            aria-label={t('loginForm.twoFactor.backupLabel')}
          />
        ) : (
          <div className="flex justify-center">
            <InputOTP
              data-qa-id="input-totp-code-login"
              maxLength={TOTP_CODE_LENGTH}
              pattern={DIGITS_ONLY}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              value={totpCode}
              disabled={loading}
              onChange={setTotpCode}
              onComplete={(value: string) => {
                void submit({ totpCode: value });
              }}
              aria-label={t('loginForm.twoFactor.codeLabel')}
            >
              <InputOTPGroup>
                {Array.from({ length: TOTP_CODE_LENGTH }, (_, index) => (
                  <InputOTPSlot key={index} index={index} className="h-11 w-11 text-lg" />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>
        )}

        {error !== '' && (
          <div
            data-qa-id="login-two-factor-error"
            role="alert"
            className="mt-2 flex items-center gap-2 rounded-md border border-error px-3 py-2 text-sm text-error font-medium"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <Button
          data-qa-id="login-two-factor-button"
          type="submit"
          className="w-full"
          disabled={loading || !canSubmit}
        >
          {loading ? t('loginForm.twoFactor.verifying') : t('loginForm.twoFactor.verify')}
        </Button>

        <div className="flex justify-between items-center text-sm">
          <button
            type="button"
            data-qa-id="login-two-factor-back"
            className="text-primary hover:underline disabled:opacity-50"
            disabled={loading}
            onClick={onBack}
          >
            {t('loginForm.twoFactor.back')}
          </button>
          <button
            type="button"
            data-qa-id="login-two-factor-toggle"
            className="text-primary hover:underline disabled:opacity-50"
            disabled={loading}
            onClick={() => {
              setUseBackupCode((previous) => !previous);
              setTotpCode('');
              setBackupCode('');
            }}
          >
            {useBackupCode
              ? t('loginForm.twoFactor.useAuthenticator')
              : t('loginForm.twoFactor.useBackupCode')}
          </button>
        </div>
      </div>
    </form>
  );
};

export default LoginCodeStep;
